import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Coins, Sparkles, AlertTriangle, ArrowRight, Crown } from "lucide-react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { registerAiCostConfirmHandler, type AiCostRequest } from "@/lib/aiCostGate";
import { CREDIT_PACKAGE_PRICES, SUBSCRIPTION_PRICES } from "@/config/pricing";
import { TIER_LIMITS } from "@/config/tiers";
import { detectUserCurrency, formatCurrency, type SupportedCurrency } from "@/lib/currencyDetection";

type Resolver = (ok: boolean) => void;

/**
 * Credits that must remain after the action for us to skip the confirmation.
 * Anything at or below this counts as "low on credits" and asks first.
 */
const LOW_CREDIT_THRESHOLD = 25;

interface PackRecommendation {
  id: string;
  credits: number;
  amount: number;
  perCredit: number;
}

function pickPacks(deficit: number, currency: SupportedCurrency) {
  const packs: PackRecommendation[] = Object.entries(CREDIT_PACKAGE_PRICES).map(([id, prices]) => {
    const info = prices[currency];
    return { id, credits: info.credits, amount: info.amount, perCredit: info.amount / info.credits };
  });

  const covering = packs.filter((p) => p.credits >= deficit);
  const cheapest = [...(covering.length ? covering : packs)].sort((a, b) => a.amount - b.amount)[0];
  const bestValue = [...(covering.length ? covering : packs)].sort((a, b) => a.perCredit - b.perCredit)[0];
  return { cheapest, bestValue };
}

export function AiCostPreviewDialog() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { creditBalance, effectiveTier } = useSubscription();
  const [request, setRequest] = useState<AiCostRequest | null>(null);
  const resolverRef = useRef<Resolver | null>(null);
  const currency = useMemo<SupportedCurrency>(() => detectUserCurrency(), []);

  const settle = useCallback((ok: boolean) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setRequest(null);
    resolve?.(ok);
  }, []);

  useEffect(() => {
    return registerAiCostConfirmHandler((next) => {
      // Unlimited plans never need a spend confirmation.
      if (creditBalance?.isUnlimited) return Promise.resolve(true);
      // Only interrupt when credits are tight: enough headroom left = run silently.
      const balance = creditBalance?.totalAvailable ?? 0;
      if (balance - next.credits > LOW_CREDIT_THRESHOLD) return Promise.resolve(true);
      return new Promise<boolean>((resolve) => {
        resolverRef.current?.(false);
        resolverRef.current = resolve;
        setRequest(next);
      });
    });
  }, [creditBalance?.isUnlimited, creditBalance?.totalAvailable]);

  const available = creditBalance?.totalAvailable ?? 0;
  const cost = request?.credits ?? 0;
  const canAfford = available >= cost;
  const deficit = Math.max(0, cost - available);
  const { cheapest, bestValue } = useMemo(() => pickPacks(Math.max(deficit, 1), currency), [deficit, currency]);

  // Cheapest plan upgrade that covers the cost going forward.
  const upgradeTier = useMemo<"pro" | "chef_master">(() => {
    if (effectiveTier === "free" && (TIER_LIMITS.pro.credits.monthly ?? 0) >= cost) return "pro";
    return effectiveTier === "free" ? "pro" : "chef_master";
  }, [effectiveTier, cost]);
  const upgradePrice = SUBSCRIPTION_PRICES[upgradeTier]?.[currency];

  const open = request !== null;

  return (
    <AlertDialog open={open} onOpenChange={(next) => { if (!next) settle(false); }}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            {canAfford ? (
              <Sparkles className="w-5 h-5 text-primary" aria-hidden />
            ) : (
              <AlertTriangle className="w-5 h-5 text-destructive" aria-hidden />
            )}
            {canAfford
              ? t("aiCostPreview.title", "Confirm credit cost")
              : t("aiCostPreview.notEnoughTitle", "Not enough credits")}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {request?.featureName}
            {request?.detail ? ` — ${request.detail}` : ""}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="rounded-xl border border-border/60 bg-muted/30 p-4 space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">{t("aiCostPreview.cost", "This action costs")}</span>
            <span className="font-semibold inline-flex items-center gap-1.5">
              <Coins className="w-4 h-4 text-primary" aria-hidden />
              {t("aiCostPreview.creditsValue", "{{count}} credits", { count: cost })}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">{t("aiCostPreview.balance", "Your balance")}</span>
            <span className="font-medium">
              {t("aiCostPreview.creditsValue", "{{count}} credits", { count: available })}
            </span>
          </div>
          {canAfford ? (
            <div className="flex items-center justify-between border-t border-border/60 pt-2">
              <span className="text-muted-foreground">{t("aiCostPreview.after", "Balance after")}</span>
              <span className="font-medium">
                {t("aiCostPreview.creditsValue", "{{count}} credits", { count: available - cost })}
              </span>
            </div>
          ) : (
            <div className="flex items-center justify-between border-t border-border/60 pt-2">
              <span className="text-muted-foreground">{t("aiCostPreview.missing", "You are missing")}</span>
              <span className="font-semibold text-destructive">
                {t("aiCostPreview.creditsValue", "{{count}} credits", { count: deficit })}
              </span>
            </div>
          )}
        </div>

        {!canAfford && (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              {t("aiCostPreview.chooseOption", "Pick the cheapest way to cover it:")}
            </p>

            {bestValue && (
              <button
                type="button"
                onClick={() => { settle(false); navigate(`/credits?pack=${bestValue.id}`); }}
                className="w-full text-left rounded-xl border border-primary/50 bg-primary/5 p-3 hover:bg-primary/10 transition-colors min-h-[56px]"
              >
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="font-medium flex items-center gap-2">
                      {t("aiCostPreview.packTitle", "{{credits}} credit pack", { credits: bestValue.credits })}
                      <Badge variant="secondary" className="text-[10px]">
                        {t("aiCostPreview.bestValue", "Best value")}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {formatCurrency(bestValue.amount / 100, currency)}
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-primary shrink-0" aria-hidden />
                </div>
              </button>
            )}

            {cheapest && cheapest.id !== bestValue?.id && (
              <button
                type="button"
                onClick={() => { settle(false); navigate(`/credits?pack=${cheapest.id}`); }}
                className="w-full text-left rounded-xl border border-border p-3 hover:bg-muted/50 transition-colors min-h-[56px]"
              >
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="font-medium">
                      {t("aiCostPreview.packTitle", "{{credits}} credit pack", { credits: cheapest.credits })}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {t("aiCostPreview.cheapest", "Cheapest option")} · {formatCurrency(cheapest.amount / 100, currency)}
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 shrink-0" aria-hidden />
                </div>
              </button>
            )}

            {upgradePrice && (
              <button
                type="button"
                onClick={() => { settle(false); navigate("/pricing"); }}
                className="w-full text-left rounded-xl border border-border p-3 hover:bg-muted/50 transition-colors min-h-[56px]"
              >
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="font-medium inline-flex items-center gap-2">
                      <Crown className="w-4 h-4 text-primary" aria-hidden />
                      {t(`aiCostPreview.upgrade.${upgradeTier}`, upgradeTier === "pro" ? "Upgrade to Pro" : "Upgrade to Chef Master")}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {formatCurrency(upgradePrice.amount, currency)} {t("aiCostPreview.perMonth", "/ month")}
                      {upgradeTier === "pro"
                        ? ` · ${t("aiCostPreview.planCredits", "{{count}} credits every month", { count: TIER_LIMITS.pro.credits.monthly })}`
                        : ` · ${t("aiCostPreview.unlimitedCredits", "Unlimited AI credits")}`}
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 shrink-0" aria-hidden />
                </div>
              </button>
            )}
          </div>
        )}

        <AlertDialogFooter>
          <Button variant="outline" onClick={() => settle(false)} className="min-h-[44px]">
            {t("common.cancel", "Cancel")}
          </Button>
          {canAfford && (
            <Button onClick={() => settle(true)} className="min-h-[44px] gap-1.5">
              <Sparkles className="w-4 h-4" aria-hidden />
              {t("aiCostPreview.confirm", "Use {{count}} credits", { count: cost })}
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
